import {
  type FunctionComponent,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';

import {
  Address,
  BankAccount,
  Credit,
  CustomerDetails,
  EmployedDetails,
  EmploymentDetails,
  FinanceDetails,
  FinanceProvider,
  FinanceRate,
  Introduction,
  Job,
  JobCompanyInfo,
  NotEmployedDetails,
  Payment,
  RetiredDetails,
  SelfEmployedDetails,
} from '@kanda-libs/ks-frontend-services';

import { URLS } from 'config';
import { useGetCache } from 'hooks';
import { ExtendedCache } from 'hooks/useGetCache';
import { selectors } from 'store';
import {
  formatToPercentage,
  getLatestLoanPaymentFromJob,
  scrollToTop,
} from 'utils';

import { getProvider, matchPathToPage, safeJsonParse } from './helpers';

export type ApplyPage = 'WELCOME' | 'TERMS_AND_CONDITIONS' | 'YOUR_INFO';

export interface ApplyFormRestFieldValuesAddress {
  month_moved_in?: string;
  year_moved_in?: string;
}

export interface ApplyFormRestFieldValues {
  address?: ApplyFormRestFieldValuesAddress[];
  bank_account?: {
    years_owned?: number;
    months_owned?: number;
  };
  confirm_account?: boolean;
  bankruptcy_iva?: boolean;
  monthly_mortgage_amount?: number;
  income_after_tax?: number;
  credit_repayments?: number;
}

export interface AddressWithSelect extends Address {
  selected?: string;
}

export type AdditionalEmploymentDetails = {
  monthly_income_after_tax: number;
  placeholder?: {
    month_started?: string;
    year_started?: string;
  };
};

export interface ExtendedEmploymentDetails extends EmploymentDetails {
  employed_details?: EmployedDetails & AdditionalEmploymentDetails;
  self_employed_details?: SelfEmployedDetails & AdditionalEmploymentDetails;
  not_employed_details?: NotEmployedDetails & AdditionalEmploymentDetails;
  retired_details?: RetiredDetails & AdditionalEmploymentDetails;
}

export type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

export interface ApplyFormCreditFieldValues extends RecursivePartial<Credit> {
  bank_account: BankAccount;
  customer_details: CustomerDetails;
  employment_details: ExtendedEmploymentDetails;
  additional_employment_details?: ExtendedEmploymentDetails[];
  finance_details: FinanceDetails;
}

export interface ApplyFormFieldValues extends ApplyFormRestFieldValues {
  credit: ApplyFormCreditFieldValues;
}

export interface ApplyContextType {
  id: string | undefined;
  job: Job | undefined;
  jobCompanyInfo: JobCompanyInfo | undefined;
  introduction: Introduction | undefined;
  rate: FinanceRate | undefined;
  loanPayment: Payment | undefined;
  loanAmount: number | undefined;
  creditId: string | undefined;
  cache: ExtendedCache | undefined;
  cachedData: ApplyFormFieldValues | undefined;
  cacheKey: string | undefined;
  rateTitle: string | undefined;
  provider: FinanceProvider | undefined;
  page: ApplyPage;
  isLoading: boolean;
  goToPage: (newPage: ApplyPage) => void;
  isIntroduced: boolean;
  isExampleJob: boolean;
  isSubmitting: boolean;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ApplyContext = createContext<ApplyContextType | null>(null);

export const useApplyContext = () =>
  useContext(ApplyContext) as ApplyContextType;

export interface ApplyProviderProps {
  children: ReactNode;
}

const ApplyProvider: FunctionComponent<ApplyProviderProps> = function ({
  children,
}) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [page, setPage] = useState<ApplyPage>('WELCOME');

  const job = useSelector(selectors.job.getEntity);
  const jobIsLoading = useSelector(selectors.job.getIsLoading);

  const jobCompanyInfo = useSelector(selectors.jobCompanyInfo.getEntity);
  const jobCompanyInfoIsLoading = useSelector(
    selectors.jobCompanyInfo.getIsLoading,
  );

  const introduction = useSelector(selectors.introduction.getEntity);
  const introductionIsLoading = useSelector(
    selectors.introduction.getIsLoading,
  );

  const globalIsLoading = useSelector(selectors.getIsLoading);
  const { id } = useSelector(selectors.getPathKey);

  const { cache, isLoading: cacheIsLoading } = useGetCache();
  const { push } = useHistory();

  const isIntroduced = useMemo(() => {
    if (!job) return false;
    return Boolean(job?.introduced_to);
  }, [job]);

  const isExampleJob = useMemo(() => job?.flow_type === 'example', [job]);

  const isLoading = useMemo(
    () =>
      jobIsLoading ||
      jobCompanyInfoIsLoading ||
      globalIsLoading ||
      cacheIsLoading ||
      (isIntroduced && introductionIsLoading),
    [
      jobIsLoading,
      jobCompanyInfoIsLoading,
      globalIsLoading,
      cacheIsLoading,
      isIntroduced,
      introductionIsLoading,
    ],
  );

  const loanPayment = useMemo(() => getLatestLoanPaymentFromJob(job), [job]);

  const creditId = useMemo(
    () => (loanPayment ? loanPayment.id : undefined),
    [loanPayment],
  );

  const loanAmount = useMemo(
    () => loanPayment?.payment_option.amount.amount,
    [loanPayment],
  );

  const rate = useMemo(() => {
    if (!job) return undefined;
    return job?.customer_options?.finance_option;
  }, [job]);

  const goToPage = useCallback((newPage: ApplyPage) => {
    setPage(newPage);
    setTimeout(() => scrollToTop(), 10);
  }, []);

  const cacheKey = useMemo(() => job?.id, [job]);

  const provider = useMemo(() => getProvider(job), [job]);

  const cachedData = useMemo(() => {
    if (!cache || !cacheKey) return undefined;
    const cacheString = cache?.[cacheKey];
    if (!cacheString) return undefined;
    return safeJsonParse<ApplyFormFieldValues>(cacheString);
  }, [cache, cacheKey]);

  const rateTitle = useMemo(() => {
    if (!rate) return undefined;
    const {
      apr,
      apr_type: aprType,
      duration,
      deferred_duration: deferred,
    } = rate;
    if (!apr || !aprType || !duration) return undefined;
    if (aprType === 'BUYNOW_PAYLATER') {
      if (!deferred) return 'Buy Now, Pay Later';
      return `Buy Now, Pay Later, ${deferred} months deferred, ${duration} repayments at ${formatToPercentage(
        apr,
      )} APR`;
    }
    if (aprType === 'INTEREST_FREE') {
      return `Interest Free, ${duration} repayments`;
    }
    return `Interest Bearing, ${duration} repayments at ${formatToPercentage(
      apr,
    )} APR`;
  }, [rate]);

  const values = useMemo(
    () => ({
      id,
      job,
      jobCompanyInfo,
      introduction,
      rate,
      loanPayment,
      loanAmount,
      creditId,
      cache,
      cachedData,
      cacheKey,
      rateTitle,
      provider,
      page,
      isLoading,
      goToPage,
      isIntroduced,
      isExampleJob,
      isSubmitting,
      setIsSubmitting,
    }),
    [
      id,
      job,
      jobCompanyInfo,
      introduction,
      rate,
      loanPayment,
      loanAmount,
      creditId,
      cache,
      cachedData,
      cacheKey,
      rateTitle,
      provider,
      page,
      isLoading,
      goToPage,
      isIntroduced,
      isExampleJob,
      isSubmitting,
      setIsSubmitting,
    ],
  );

  const pageRef = useRef<ApplyPage>(page);
  const { pathname } = window.location;
  useEffect(() => {
    const matchedPage = matchPathToPage(pathname);
    if (!matchedPage) return;
    if (matchedPage === pageRef.current) return;
    pageRef.current = matchedPage;
    setPage(matchedPage);
  }, [pathname]);

  useEffect(() => {
    if (!job || !id) return;
    const option = job.customer_options?.checkout_option;
    const status = job?.finance_status;
    if (status !== 'not_applied') {
      push(generatePath(URLS.job, { id }));
      return;
    }
    if (option === 'apply_for_finance') return;
    push(generatePath(URLS.job, { id }));
  }, [job, id, push]);

  return (
    <ApplyContext.Provider value={values}>{children}</ApplyContext.Provider>
  );
};

export default ApplyProvider;
